// plugins/translatorPlugin.js

import { reactive, watch } from 'vue';
import Translator from '../utils/translation';
import store from '@/store'; // Import the store

const TRANSLATION_BUNDLES = {
  admin: '/locales/admin-translations.json',
  kiosk: '/locales/translations.json', // customer-facing; also used for public pages (receipt, setup)
};

/**
 * Resolve which translation bundle a path needs.
 * The admin UI is reached from /admin/* and from the SkyTab OAuth callback (/oauth/callback),
 * which logs the user in and then redirects into /admin/dashboard without a full page reload.
 */
export const translationContextForPath = (path = '') => {
  if (path.includes('/admin') || path.startsWith('/oauth')) {
    return 'admin';
  }
  return 'kiosk';
};

const createTranslatorPlugin = (options = {}) => {
  const translator = reactive(new Translator(store.state.currentLanguage, options.translations || {}, {}, options.configTexts));

  let loadedContext = null;
  let pendingLoad = null;

  /**
   * Make sure the bundle for the given path is loaded into the translator.
   * Safe to call on every navigation: no-op when the context hasn't changed,
   * and concurrent calls share one in-flight fetch.
   */
  const ensureTranslations = async (path) => {
    const context = translationContextForPath(path);
    if (context === loadedContext) return;
    if (pendingLoad) {
      await pendingLoad;
      if (context === loadedContext) return;
    }

    pendingLoad = (async () => {
      const response = await fetch(TRANSLATION_BUNDLES[context]);
      if (!response.ok) {
        throw new Error(`Failed to load ${context} translations (${response.status})`);
      }
      translator.setTranslations(await response.json());
      loadedContext = context;
    })();

    try {
      await pendingLoad;
    } finally {
      pendingLoad = null;
    }
  };

  // Watch for language changes in the store
  watch(() => store.state.currentLanguage, (newLanguage) => {
    translator.setLanguage(newLanguage);
  });

  return {
    translator,
    ensureTranslations,
    install(app) {
      app.config.globalProperties.$t = (key, params) => {
        const text = translator.translate(key);
        if (!params || typeof text !== 'string') return text;
        return text.replace(/%\{(\w+)\}/g, (match, name) => (name in params ? params[name] : match));
      };
      app.config.globalProperties.$setLanguage = (language) => {
        store.dispatch('setLanguage', language);
        translator.setLanguage(language);
      };
      app.config.globalProperties.$setConfigTexts = (configTexts) => translator.setConfigTexts(configTexts);
      app.config.globalProperties.$addTranslations = (language, translations) => translator.addTranslations(language, translations);
      app.config.globalProperties.$verifyTranslations = () => translator.verifyTranslations();

      app.provide('translator', translator);
    }
  };
};

export default createTranslatorPlugin;
